
import { debugLogger } from '@/utils/debugLogger';

/**
 * Generate a consistent hash from user ID for privacy
 */
export const generateUserHash = async (userId: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(userId);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  // Use first 16 characters for shorter path
  return hashHex.substring(0, 16);
};

/**
 * Generate photo filename with new structure
 */
export const generatePhotoFilename = (
  username: string,
  timestamp: number,
  variant: 0 | 1 | 2 | 3 | 'micro-avatar' | 'small-thumb' | 'medium-card' | 'large-display'
): string => {
  // Sanitize username for filename
  const sanitizedUsername = username.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${sanitizedUsername}-${timestamp}-${variant}.jpg`;
};

/**
 * Generate complete photo path
 */
export const generatePhotoPath = async (
  userId: string,
  username: string,
  timestamp: number,
  variant: 0 | 1 | 2 | 3 | 'micro-avatar' | 'small-thumb' | 'medium-card' | 'large-display'
): Promise<string> => {
  const userHash = await generateUserHash(userId);
  const filename = generatePhotoFilename(username, timestamp, variant);
  return `${userHash}/${filename}`;
};

/**
 * Extract base URL from any variant URL
 */
export const extractBaseUrl = (variantUrl: string): string => {
  // Remove variant suffix to get base URL (both old and new)
  return variantUrl
    .replace('-ultra-thumb.jpg', '.jpg')
    .replace('-medium-display.jpg', '.jpg')
    .replace('-full-quality.jpg', '.jpg')
    .replace('-micro-avatar.jpg', '.jpg')
    .replace('-small-thumb.jpg', '.jpg')
    .replace('-medium-card.jpg', '.jpg')
    .replace('-large-display.jpg', '.jpg');
};

/**
 * Check if URL uses new structure
 */
export const isNewStructureUrl = (url: string): boolean => {
  // New structure: hash/username-timestamp-variant.jpg
  const pathPart = url.split('/profile_photos/')[1];
  if (!pathPart) return false;
  
  const parts = pathPart.split('/');
  if (parts.length !== 2) return false;
  
  const [hash, filename] = parts;
  
  // Hash should be 16 hex characters
  const hashPattern = /^[a-f0-9]{16}$/;
  if (!hashPattern.test(hash)) return false;
  
  // Filename should match: username-timestamp-variant.jpg (old or new variants)
  const filenamePattern = /^[a-z0-9]+-\d+-(?:full-quality|medium-display|ultra-thumb|micro-avatar|small-thumb|medium-card|large-display)\.jpg$/;
  return filenamePattern.test(filename);
};

/**
 * Convert old URL structure to new format (for migration)
 */
export const convertOldUrlToNew = async (
  oldUrl: string,
  userId: string,
  username: string
): Promise<string> => {
  debugLogger.debug('[photoUrlUtils] Converting old URL to new format:', {
    oldUrl,
    userId,
    username
  });
  
  try {
    // Extract timestamp from old URL if possible
    const timestampMatch = oldUrl.match(/(\d+)-/);
    const timestamp = timestampMatch ? parseInt(timestampMatch[1]) : Date.now();
    
    // Determine variant from old URL (map old to new)
    let variant: 'micro-avatar' | 'small-thumb' | 'medium-card' | 'large-display' = 'large-display';
    if (oldUrl.includes('-ultra-thumb.jpg')) {
      variant = 'small-thumb'; // Old ultra-thumb -> new small-thumb
    } else if (oldUrl.includes('-medium-display.jpg')) {
      variant = 'medium-card'; // Old medium-display -> new medium-card
    } else if (oldUrl.includes('-full-quality.jpg')) {
      variant = 'large-display'; // Old full-quality -> new large-display
    }
    
    // Generate new path
    const newPath = await generatePhotoPath(userId, username, timestamp, variant);
    
    // Replace the path in the URL
    const baseUrl = oldUrl.split('/storage/v1/object/public/profile_photos/')[0];
    const newUrl = `${baseUrl}/storage/v1/object/public/profile_photos/${newPath}`;
    
    debugLogger.debug('[photoUrlUtils] Converted URL:', {
      oldUrl,
      newUrl,
      variant,
      timestamp
    });
    
    return newUrl;
  } catch (error) {
    debugLogger.error('[photoUrlUtils] Error converting URL:', error);
    return oldUrl; // Return original URL if conversion fails
  }
};
